<template>
  <PageTemplate>
    <template v-slot:page-content>
      <b-container fluid id="page-container">
        <div id="content-container-with-border">
          <h1>PayPoint Privacy Statement</h1>
          <h1>When is information collected?</h1>
          This Privacy Statement tells you what to expect when the PayPoint
          Group ("PayPoint") collects personal information. It applies to
          information we collect about:
          <ul>
            <li>PayPoint Retailers providing services to PayPoint.</li>
            <li>
              PayPoint Agents using PayPoint Retailer Services (e.g. EPOS).
            </li>
            <li>Visitors to our websites.</li>
          </ul>
          It is intended to give you a better understanding of the data we use
          and what we do with your data.

          <h1>Definitions</h1>
          <p>For the purpose of this Privacy Notice;</p>
          <p>
            <strong>Client</strong> means an organisation using a PayPoint
            service to obtain payment from or make payments to a consumer or
            business.
          </p>
          <p>
            <strong>Customer</strong> means a consumer or business that is a
            customer of a client.
          </p>
          <p>
            <strong>PayPoint</strong> means a member of the PayPoint Group.
            Members of the PayPoint Group are listed on the website under
            PayPoint Companies Regulatory Information.
          </p>
          <p></p>
          <p>
            <strong>PayPoint Retailer</strong> means an organisation providing
            services to PayPoint.
          </p>
          <p></p>
          <p>
            <strong>PayPoint Agent</strong> means an individual or organisation
            using a PayPoint Retailer Service, such as card processing or EPOS.
          </p>
          <p>
            <strong>Transaction Data</strong> means information in relation to
            the transaction processed by PayPoint.
          </p>
          References to "personal data" and "data subject" shall have the
          meaning assigned to them in the Data Protection Act 2018.

          <h1>On what basis will we use your information?</h1>
          We will only use your information where we have your consent or we
          have another lawful reason for using it. These reasons include where
          we:
          <ul>
            <li>need to pursue our legitimate interests;</li>
            <li>
              need to process the information to carry out an agreement we have
              with you;
            </li>
            <li>
              need to process the information to comply with a legal obligation;
            </li>
            <li>
              believe the use of your information as described is in the public
              interest, e.g. for the purpose of preventing or detecting crime;
            </li>
            <li>
              need to establish, exercise or defend our legal rights; or
            </li>
            <li>need to use your information for our insurance purposes.</li>
          </ul>

          <h1>Disclosure of personal information</h1>
          We may from time to time disclose information as follows;
          <ul>
            <li>
              Your personal data to appropriate investigatory and regulatory
              authorities.
            </li>
            <li>
              Your information to a supplier if required to use a PayPoint
              service, provide a service to PayPoint or obtain support for
              PayPoint services.
            </li>
            <li>
              If you have provided your business information such as name,
              contact name, location, contact number we may give this together
              with details of individual transactions to clients and customer of
              clients in relation to questions or queries in associated with the
              PayPoint services.
            </li>
            <li>
              If you use PayPoint Retailer Services, we may give your business
              information to third parties such as acquirers or your EPOS
              wholesalers if you use our services.
            </li>
            <li>
              If you are a payment agent we may provide your personal data to
              the Financial Conduct Authority as part of the payment agent
              accreditation process and maintenance of the appropriate
              registrations.
            </li>
            <li>
              If you are a customer of a client we may provide your information
              to your service provider (the client) and the appropriate
              authorities.
            </li>
          </ul>

          <h1>Monitoring</h1>
          <p>
            PayPoint electronic communication systems are monitored without user
            consent to: investigate or detect unauthorised use; prevent or
            detect crime; establish the existence of facts relevant to PayPoint;
            or ascertain compliance with regulatory practices relevant to the
            business of PayPoint.
          </p>
          <p>
            Calls to PayPoint are recorded for operational and training
            purposes. Calls are not processed against the individual data
            subject.
          </p>

          <h1>PayPoint Retailer Information</h1>
          <p>
            <strong>Validation</strong> - In order to provide services to
            PayPoint, a PayPoint Retailer is required to provide personal data
            to PayPoint. PayPoint will need to maintain this information to
            verify the identity of the PayPoint Retailer and make certain
            checks. We may verify your information by using verification and
            identity checking services such as Experian or Creditsafe. PayPoint
            may also be required to disclose this information to the appropriate
            regulatory authorities. As a PayPoint Retailer your details will
            also appear as a payment agent on the Financial Conduct Authority
            register.
          </p>
          <p>
            PayPoint may use this personal data to perform checks and obtain
            reports in relation to the PayPoint Retailer. This may include
            credit checks as specified in the PayPoint Retailer consent form
            previously provided to you.
          </p>
          <p>
            <strong>My Payment</strong> - A copy of the current version of the
            consent form is available on My PayPoint. Further information in
            relation to the use of PayPoint Retailer information is available in
            the Privacy FAQs – PayPoint Retailer available on My PayPoint. You
            can set your own preferences for disclosure to your employees of
            your business information accessible by using the settings available
            on MyPayPoint.
          </p>
          <p>
            <strong>Disclosures</strong> - We may disclose information you
            supply to regulatory bodies, investigatory authorities such as the
            police, HMRC, your acquirer (if you use our card services) and
            public health authorities. We may also disclose your information to
            provide you with operational support for products and/or services
            such as maintenance call outs or your bank details to our payment
            service providers so you can make and receive payments from us.
          </p>
          <p>
            <strong>PayPoint Group offers</strong> - We may use your information
            to provide you with details of products or services provided by the
            PayPoint Group relevant to your business.
          </p>
          <p>
            <strong>Third party offers</strong> - From time to time we give you
            the opportunity to participate in offers and services provided by
            third parties. If you decide to participate you will contract on the
            third-party terms but we may disclose your information to the third
            party to enable them to offer their services to you. We may need to
            provide such third parties with information when you change, amend
            or update your personal details and/or your services with PayPoint
            as it may impact the terms they offer. You should review any
            third-party terms to make sure they are suitable for your needs.
          </p>
          <p>
            <strong>Regulatory and Compliance</strong> - We are required to
            process your personal data in order for you to provide services to
            us and for us to provide services to you under the terms of your
            agreements with us. For regulatory and compliance purposes we are
            not able to automatically delete data that relates to payment
            transactions processed by you or accreditation information provided
            by you.
          </p>
          <p>
            <strong>Service updates</strong> - We will use information provided
            by you to provide you with service updates and information to enable
            you to provide services to us or for us to provide services to you.
            We request you provide us with your business e-mail address or
            business contact details for these communications.
          </p>

          <h2>COVID 19</h2>
          <p>
            During the COVID 19 pandemic we may ask you for information as to
            whether your outlet or premises have been impacted by the need of
            individuals to isolate or otherwise take action in relation to COVID
            19.
            <strong
              >We do not require you to give us any information in relation to
              individuals.</strong
            >
            But we do request you contact us if our representatives have been in
            contact with someone who is subsequently required to isolate or your
            business needs to temporarily suspend services to comply with COVID
            19 guidelines. Any such information is recorded against the site
            reference but not against individuals
          </p>

          <h2>Marketing to a PayPoint Retailer</h2>
          <p>
            PayPoint may use PayPoint Retailer contact information to provide
            services to, market to or enhance services to the PayPoint Retailer.
            You may elect to have marketing services in different forms such as
            text or email or via social media contact groups via the Preference
            Centre.
          </p>
          <p>
            PayPoint does not provide your information to third parties for
            marketing purposes without your consent.
          </p>
          <p>
            If you have agreed, PayPoint may from time to time share the
            PayPoint Retailer contact information with third parties to offer
            services to the PayPoint Retailer. If a PayPoint Retailer contact
            does not wish to receive such communications they can unsubscribe
            using the link at the bottom of email or make changes via the
            Preference Centre.
          </p>

          <h1>Data export or transfer</h1>
          <p>
            In order to provide services or support to Clients, PayPoint
            Retailers and consumers PayPoint may use service providers
            implementing "cloud" based and other technology. These services may
            include operational, technical and administrative support that is
            based outside the EEA. Any transfer of personal data that is outside
            the EEA will be in accordance with the recommended data transfer
            models applicable at the time.
          </p>

          <h1>PayPoint Agents</h1>
          <h2>EPoS Services</h2>
          <p>
            All personal data submitted by the PayPoint Retailer or PayPoint
            Agent in relation to their use of the PayPoint EPoS Services is
            confidential and controlled by the PayPoint Retailer or PayPoint
            Agent. PayPoint Retailers and PayPoint Agents have full ownership of
            and access to all their own trading data. In common with other EPoS
            providers, PayPoint retains the right to use trading data from
            PayPoint Retailers' and PayPoint Agents' use of the system and
            services for product improvements, management information, data
            aggregation and business development purposes.
          </p>
          <h2>Debit and credit card services</h2>
          <p>
            If you use our debit and credit card services we may disclose your
            personal data associated with that service such as your contact
            details to your acquirer or if so requested to the payment card
            schemes such as MasterCard Inc or Visa Europe Limited.
          </p>
          <h1>My.PayPoint.com</h1>
          <p>
            My.PayPoint.com provides PayPoint Retailers and PayPoint Agents with
            access to information about the services we provide to you and you
            provide to us, including information on sales performance and
            financial calculations.
          </p>
          <p>
            By authorising and providing access of the My.PayPoint.com website
            to other users in connection with your business, you confirm the
            users have consented to the PayPoint Privacy Statement.
          </p>
          <p>
            In order to provide the My.PayPoint.com services, PayPoint will use
            the data submitted to us by the PayPoint Agent and/or PayPoint
            Retailer in relation to the PayPoint services, including any trading
            data. PayPoint Agents have full ownership of and access to all their
            own trading data. PayPoint retains the right to use trading data
            from PayPoint Retailers’ and PayPoint Agents’s use of the system and
            services for product improvements, management information, data
            aggregation and business development purposes.
          </p>
          <h1>Complaints or queries</h1>
          <p>
            PayPoint applies transparency when collecting and using personal
            information. We have prepared as series of FAQs to enable you to
            understand how we use your data. If you have concerns over the use
            of your personal data please contact us.
          </p>
          <p>
            This Privacy Statement and any Privacy FAQs are not an exhaustive
            list of all aspects of PayPoint’s collection and use of personal
            information. If you wish to have any additional information or
            explanation please contact us via the contact details set out below
            or your normal contact.
          </p>
          <p>
            If you want to request information about our privacy policies or a
            complaint you can e-mail us at
            <a href="mailto:privacy@paypoint.com">privacy@paypoint.com</a> or
            write to us at:
          </p>

          <p>
            Data Protection Officer<br />PayPoint Group<br />1 The Boulevard<br />Shire
            Park<br />Welwyn Garden City<br />Hertfordshire<br />AL7 1EL
          </p>
          <p>
            If you have a complaint in relation to the use of your personal data
            you may contact us in the first instance. If you are still not happy
            we have resolved the matter you may also contact the Information
            Commissioner’s Office. Contact details are available on the
            Information Commissioner’s website.
          </p>

          <h1>Access requests</h1>
          <p>
            Individuals can find out if we hold any personal information by
            making a "subject access request" under the Data Protection Act
            2018. If we do hold information about you we will:
          </p>
          <ul>
            <li>give you a description of it;</li>
            <li>tell you why we are holding it;</li>
            <li>tell you who it could be disclosed to; and</li>
            <li>
              let you have a copy of the information in an intelligible form.
            </li>
          </ul>
          <p>
            To make a request to PayPoint for any personal information we may
            hold, you need to put the request in writing addressing it to our
            Data Protection Officer at the address provided below. We cannot
            disclose information in relation to other data subjects, client
            information or client customer information under such a request. We
            will remove information relating to other data subject or other
            organisation from the information we send you.
          </p>
          <p>
            If you agree, we will try to deal with your request informally, for
            example by providing you with the specific information you need over
            the telephone. If you have e-mailed us we will respond by e-mail.
            Please note e-mail communications are no guaranteed to be secure due
            to the nature of the communication method.
          </p>
          <p>
            The Information Commissioner has provided a guide on its website to
            data subject access requests. This provides indications of the
            circumstances when an access request can be limited or declined. For
            example, a data subject access request does not entitle the data
            subject to information relating to or confidential to a third party.
            Data subject access requests are available to individuals but not
            businesses.
          </p>
          <h2>Other use of your rights</h2>
          <p>
            Under the Data Protection Act 2018, you have rights as an individual
            which you can exercise in relation to the information we hold about
            you. You can read more about these rights here –
            <a
              href="https://ico.org.uk/for-the-public/is-my-information-being-handled-correctly/"
              >https://ico.org.uk/for-the-public/is-my-information-being-handled-correctly/</a
            >
          </p>
          <h1>General</h1>
          <h2>People who e-mail us</h2>
          <p>
            Any e-mail sent to us, including any attachments, may be monitored
            and used by us for reasons of security and for monitoring compliance
            with PayPoint policies. E-mail monitoring or blocking software may
            also be used. Please be aware that you have a responsibility to
            ensure that any e-mail you send to us is within the bounds of the
            law.
          </p>
          <h2>People who call our call centre</h2>
          <p>
            When you call the PayPoint’s call centre we may collect caller
            identification information. We use this information to help improve
            its efficiency and effectiveness. We may record your call to us for
            operational and training purposes. Recordings of any such calls is
            not retained against or save to the name of the individual caller.
          </p>
          <h2>Visitors to our websites</h2>
          <p>
            When you visit our websites
            <a href="https://my.paypoint.com">my.paypoint.com</a> and
            <a href="https://www.paypoint.com">www.paypoint.com</a> please see
            the relevant website privacy notice for details of cookies and other
            information specific to the website.
          </p>
          <h2>Links to other websites</h2>
          <p>
            This Privacy Notice does not cover the links within any PayPoint
            website or documentation linking to other websites. We encourage you
            to read the privacy statements on any other websites you visit.
          </p>
          <h2>Changes to this privacy notice</h2>
          <p>
            We keep our privacy notice under regular review so please check for
            notices of changes on My PayPoint or in our newsletters to you.
          </p>
          <h2>Keeping your information secure</h2>
          <p>
            PayPoint takes its obligations to keep data secure seriously. It is
            ISO 27001 accredited and a PCI compliant organisation for the
            purpose of processing card payment data.
          </p>
          <h2>Governing Law</h2>
          <p>
            This Privacy Notice shall be governed by and construed in accordance
            with English law and the parties hereby submit to the exclusive
            jurisdiction of the English courts in respect of all matters arising
            in connection herewith.
          </p>
          <h2>Retention</h2>
          <p>
            Information we hold in relation to payments is retained for
            statutory and regulatory purposes. Payment information may be
            retained for periods of 3, 5 and as applicable 10 years or such
            other period as required by law. Information relating to tax records
            may be held for 7 years or such other period are required by law.
            Certain payment information is held for 7 years or up to 10 years
            post completion of the contract. Where we hold information on behalf
            of a Client, the retention period is as specified by the Client. We
            can provide more information on retention periods for specific types
            of information if you email
            <a href="mailto:privacy@paypoint.com">privacy@paypoint.com</a>. Our
            retention policy is updated from time to time to reflect changes in
            our obligations and legal duties.
          </p>
          <h1>How to contact us</h1>
          <p>
            If you want to request information about our privacy policies or a
            complaint you can e-mail us at
            <a href="mailto:privacy@paypoint.com">privacy@paypoint.com</a> or
            write to us at:
          </p>

          <p>
            Data Protection Officer <br />PayPoint Group <br />1 The Boulevard
            <br />Shire Park <br />Welwyn Garden City <br />Hertfordshire
            <br />AL7 1EL
          </p>
          <p>
            If you are not happy with our response to any query or wish to
            complain you may also contact the Information Commissioner’s Office
            via
            <a href="https://ico.org.uk/concerns/"
              >https://ico.org.uk/concerns/</a
            >
            or by using the contact details at
            <a href="https://ico.org.uk/global/contact-us/"
              >https://ico.org.uk/global/contact-us/</a
            >
          </p>
          <h1>Changes and Updates</h1>
          <p>
            We may update this Privacy Statement from time to time and in
            particular as the legal environment in relation to your information
            changes.
          </p>
          <p>This Privacy Statement was last updated on 20 July 2021</p>
        </div>
      </b-container>
    </template>
  </PageTemplate>
</template>

<script>
import PageTemplate from "Components/PageTemplate";
import { setPageTitle } from "Utils/pageTitle.js";

export default {
  name: "TermsAndConditions",
  components: {
    PageTemplate,
  },
  created () {
    setPageTitle('Privacy Policy');
  },
  data() {
    return {
      data: null,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "Design/_variables.scss";
@import "Design/paypoint-form-common-styles.scss";

#paypoint-terms-and-conditions-page {
  background-color: $color-brand-light-blue-grey;
  position: relative;
  height: 100%;
}
</style>
